import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useEffect,
} from "react";
import {
  ConversationMode,
  InterViewCurrentTask,
  InterviewInitState,
  InterviewStatus,
} from "../types/interview.type";
import { toast } from "react-toastify";
import { useGenerateSpeachMutation, useReplayMessageMutation } from "../redux/api/interview.api";
import { useAppSelector } from "../redux/hooks";
import { useNavigate } from "react-router-dom";
import { SessionStorage } from "../types/general.type";
import { AudioPlayer, ChatMessages, Interview, InterviewContextType } from "../types/interview.context.type";
import { setupAvatarAudioMediaSession, cleanupAvatarAudioMediaSession } from "../utils/mediaSession";

// Create Interview Context
const InterviewContext = createContext<InterviewContextType | undefined>(
  undefined
);


// Create Interview Provider Component
export const InterviewProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {

  // Get navigation instance
  const navigate = useNavigate();

  // Get user data from redux store
  const currUser = useAppSelector((state) => state.auth.user);
  // General interview state
  const [interview, setInterview] = useState<Interview>({
    isEnabledRecordButton: false,
    isEnabledSubmitButton: false,
    isChatInputsEnabled: false,
    chatMessages: [],
    interviewData: undefined,
    showProcessing: false,
    selectedLanguage: "",
    currentTask: InterViewCurrentTask.NO_TASK_YET,
    isMuted: false,
    isVideoOff: false,
    isAISoundMuted: false,
    isFullScreen: false,
    selectedInterviweer: null,
    audioRef: React.useRef<HTMLAudioElement | null>(null),
    threadId: "",
    sentData: null,
    recordingStarted: false,
    chunks: [],
    videoRef: React.useRef<HTMLVideoElement | null>(null),
    isVideoSimulatorAvailable: true,
    isMessageSent: false,
    isSpeechRecognitionAvailable: true,
    closed: false,
    isOnline: true
  });

  // Speech recognition state
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
  const [replyMessage] = useReplayMessageMutation();
  const [generateSpeech] = useGenerateSpeachMutation();

  // Define ref with correct typing
  const mutationRef = useRef<ReturnType<typeof replyMessage | typeof generateSpeech> | null>(null);


  // Final transcript state
  const [finalTranscript, setFinalTranscript] = useState<string>("");

  // State to manage messages
  const [messages, setMessages] = useState<ChatMessages[]>([]);


  // Time state to manage is interviewee is not responding
  const [time, setTime] = useState(0);

  // Get initial data from navigation state
  // const location = useLocation();
  const state = JSON.parse(sessionStorage.getItem(SessionStorage.INTERVIEW_STATE) || "{}") as InterviewInitState;


  const { audioRef, isVideoSimulatorAvailable, selectedInterviweer, currentTask, setUp, } = interview;
  const conversationMode = setUp?.conversationMode;

  // Use effect to start interview or to redirect user to interview setup page if interview is not initialized
  useEffect(() => {
    const initialized = state?.initialResponse;
    if (!initialized) {
      sessionStorage.removeItem(SessionStorage.INTERVIEW_STATE);
      navigate("/interview-setup");
    }
    else {
      const { initialResponse } = state
      if (!initialResponse?.threadId) {
        toast.error("Thread ID  missing");
        navigate("/interview-setup");

        return;
      }
      resetInterview();
      if (state?.setup.conversationMode === ConversationMode.CHAT)
        startInterview();
    }
  }, []);


  // Function to start the interview
  const startInterview = async () => {

    resetInterview();
    const { sentData, selectedInterviweer, initialResponse } = state
    const response = state?.initialResponse?.response;
    if (!response) {
      toast.error("No interviewer response found");
      navigate("/interview-setup");
      return;
    }
    updateInterview({
      sentData,
      selectedInterviweer,
      interviewInitResponse: initialResponse,
      interviewStatus: InterviewStatus.INTERVIEW_STARTED,
      threadId: state?.initialResponse?.threadId || "",
      setUp: {
        isAuto: !!state.setup.isAuto,
        conversationMode: state.setup?.conversationMode || ConversationMode.CHAT,
        started: true,
        paused: false,
        completed: false,
      }
    });
    if (state.setup.conversationMode == ConversationMode.CHAT) {
      appendMessage(response, "bot");
      sessionStorage.removeItem(SessionStorage.SENDING)
    }
    else
      updateInterview({ showProcessing: true })

    if (state.setup.conversationMode !== ConversationMode.CHAT) {
      speak(response);
    }
    else {
      updateInterview({
        currentTask: InterViewCurrentTask.BOT_TYPING,
        isChatInputsEnabled: true,
        isEnabledSubmitButton: true,
      });
    }
  };

  // Function to reset the interview state
  const resetInterview = () => {
    sessionStorage.removeItem(SessionStorage.SENDING);
    sessionStorage.removeItem(SessionStorage.LISTENING);
    if (mutationRef.current) {
      mutationRef.current.abort();
      mutationRef.current = null;
    }
    if (audioRef.current) {
      cleanupAvatarAudioMediaSession(audioRef.current);
      audioRef.current.pause();
      audioRef.current = null;
    }
    setMessages([]);
    setFinalTranscript("");
    if (recognition) {
      recognition.stop()
      setRecognition(null);
    }
    setTime(0);
    updateInterview({
      recordingStarted: false,
      isEnabledRecordButton: false,
      isEnabledSubmitButton: false,
      isChatInputsEnabled: false,
      chatMessages: [],
      interviewData: undefined,
      showProcessing: false,
      selectedLanguage: "",
      currentTask: InterViewCurrentTask.NO_TASK_YET,
      isMuted: false,
      isVideoOff: false,
      isAISoundMuted: false,
      isFullScreen: false,
      selectedInterviweer: null,
      threadId: "",
      sentData: null,
      setUp: undefined,
      interviewInitResponse: undefined,
      interviewStatus: undefined,

    })
  }


  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (currentTask === InterViewCurrentTask.YOU_SPEAKING || currentTask === InterViewCurrentTask.YOU_TYPING) {
      setTime(0);
      if (timer) clearInterval(timer as NodeJS.Timeout);
      timer = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [currentTask]);



  useEffect(() => {
    if (time >= 60 && time / 60 === Math.floor(time / 60)) {
      toast.info("Please reply. Interviewer is waiting for your response");
    }
  }, [time]);

  const updateInterview = (interview: Partial<Interview>) => {
    setInterview((prev) => ({
      ...prev,
      ...interview,
    }));
  };


  const getMessageTime = (): string => {
    const now = new Date();
    return now.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }


  const appendMessage = (message: string, user: string) => {
    const { selectedInterviweer } = state
    const sender = user === "user" ? "user" : selectedInterviweer?.name || "";
    const imageUrl = user === "user" ? (currUser?.profilePicturePath || '') : selectedInterviweer?.image || "./logo/green-logo.png";
    setMessages((prev) => [
      ...prev,
      { sender: sender, date: getMessageTime(), imageUrl: imageUrl, text: message },
    ]);
  }

  const sendMessage = async (message: string) => {
    const sending = sessionStorage.getItem(SessionStorage.SENDING)


    if (sending) {
      toast.info("Please wait. Your message is being sent");
      return;
    }
    else {
      sessionStorage.setItem(SessionStorage.SENDING, "true")
    }

    sessionStorage.removeItem(SessionStorage.LISTENING)

    updateInterview({
      showProcessing: true,
      isChatInputsEnabled: false,
      isEnabledRecordButton: false,
      isEnabledSubmitButton: false,
      currentTask: InterViewCurrentTask.PROCESSING_ANSWER,
      recordingStarted: false,
    });
    recognition?.stop()
    try {

      if (messages[messages.length - 1]?.sender !== "user")
        appendMessage(message, "user");
      else if (messages[messages.length - 1]?.text !== message)
        return;
      const promise = replyMessage({ threadId: state?.initialResponse?.threadId || "", message: message });
      mutationRef.current = promise;
      const response = await promise.unwrap();
      updateInterview({
        interviewData: response,
        isMessageSent: true,
      });
      if (response.isProvidingScore) {
        setUp &&
          updateInterview({
            setUp: {
              ...setUp,
              completed: conversationMode === ConversationMode.CHAT,
            },
            currentTask: InterViewCurrentTask.INTERVIEW_COMPLETTED,
            interviewStatus: InterviewStatus.INTERVIEW_COMPLETED,
          });


      }
      if (conversationMode !== ConversationMode.CHAT)
        speak(response?.response || "", response.isProvidingScore);
      else {
        updateInterview({
          showProcessing: false,
          isChatInputsEnabled: true,
        })
        appendMessage(response.response || "", "bot");
      }
    } catch (error: any) {
      updateInterview({ isChatInputsEnabled: false, isEnabledSubmitButton: false, isMessageSent: false, showProcessing: false });
      if (error.status === "UNKOWN_ERROR")
        toast.error(
          "Error occurred while sending your reply. Please try again"
        );
    }
    finally {
      sessionStorage.removeItem(SessionStorage.SENDING);
    }
  }


  useEffect(() => {
    if (!isVideoSimulatorAvailable && setUp?.conversationMode === ConversationMode.VIDEO) {
      startInterview();
    }
  }
    , [isVideoSimulatorAvailable]);
  const speak = async (message: string, isProvidingScore = false) => {
    if (interview.closed)
      return;


    if (audioRef.current) {
      cleanupAvatarAudioMediaSession(audioRef.current);
      audioRef.current.pause();
      audioRef.current = null; // Reset audio to the beginning
    }

    if (!message) {
      return;
    }

    try {

      const { selectedInterviweer } = state;
      const promise = generateSpeech({
        voiceName: selectedInterviweer?.voiceName || "en-US-AndrewMultilingualNeural",
        message: message,
      })

      mutationRef.current = promise;
      const response = await promise.unwrap();
      if (interview.closed) {
        return;
      }
      if (!response) {
        return;
      }

      const audioBlob = new Blob([response], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      // Setup media session to prevent notifications on mobile devices
      setupAvatarAudioMediaSession(audio);

      audio.onended = () => {
        if (isProvidingScore) {
          if (!setUp?.completed)
            setUp && updateInterview({
              setUp: {
                ...setUp,
                completed: true,
              },
            })
        } else {
          updateInterview({
            recordingStarted: state?.setup?.isAuto,
            currentTask: InterViewCurrentTask.WAITING_YOU_FOR_REPLY,
            isEnabledRecordButton: true,
            isChatInputsEnabled: true,
          });
          toast.info("You can now reply");
          if (state?.setup?.isAuto)
            sessionStorage.setItem(SessionStorage.LISTENING, "true")
        }
      };

      audio.onplay = () => {
        updateInterview({
          showProcessing: false,
          currentTask: InterViewCurrentTask.BOT_SPEAKING,
        });
      };

      audioRef.current = audio;
      appendMessage(message, "bot");
      await audio
        .play()
        .then(() => {
          updateInterview({
            currentTask: InterViewCurrentTask.BOT_SPEAKING,
          });
        })


    } catch (error: any) {
      updateInterview({ currentTask: InterViewCurrentTask.NO_TASK_YET })
      if (error.status === "UNKOWN_ERROR")
        toast.error(
          "Error occurred while generating speech. Please try againn"
        );
    }

  };







  return (
    <InterviewContext.Provider
      value={{
        finalTranscript,
        setFinalTranscript,
        recognition,
        setRecognition,
        appendMessage,
        interview: {
          ...interview,
          chatMessages: messages,
        },
        updateInterview,
        sendMessage,
        speak,
        resetInterview,
        startInterview,
        setMessages,
        mutationRef: mutationRef,
      }}
    >
      {children}
    </InterviewContext.Provider>
  );
};

// Custom Hook to Access Interview Context
export const useInterview = (): InterviewContextType => {
  const context = useContext(InterviewContext);
  if (!context) {
    throw new Error("useInterview must be used within an InterviewProvider");
  }
  return context;
};


